import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { FeatureFlagApi } from "../api/Global/FeatureFlag";

interface FeatureFlagContextType {
  flags: { [key: string]: boolean };
  loading: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const [flags, setFlags] = useState<{ [key: string]: boolean }>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatureFlags = async () => {
      try {
        // Feature flags are resolved server-side (see MIDAS_3_APIs FeatureFlagController)
        // so the Azure App Configuration connection string/access key never needs to be
        // shipped to the browser.
        const featureFlags = await FeatureFlagApi.getFeatureFlags();
        setFlags(featureFlags);
      } catch (error) {
        console.error(
          "Failed to fetch feature flags:",
          error instanceof Error ? error.message : "Unknown error",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchFeatureFlags();
  }, []);

  return (
    <FeatureFlagContext.Provider value={{ flags, loading }}>{children}</FeatureFlagContext.Provider>
  );
};

// Custom hook to consume flags inside components
export const useFeatureFlag = (flagName: string) => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlag must be used within a FeatureFlagProvider");
  }
  const { flags, loading } = context;
  return { isEnabled: !!flags[flagName], loading };
};
